import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Chip, Divider, Popover } from '@mui/material';
import { styled as muiStyled, css, useTheme } from '@mui/material/styles';
import React from 'react';

import Text from '~c/components/base/text';

export interface StatusListItem {
  name: string;
  status: string;
  color: string;
  /** Optional callback when the item row is clicked (e.g. navigate to assessment) */
  onClick?: () => void;
}

interface Props {
  className?: string;
  /** Summary text shown as clickable trigger, e.g. "3 of 10 non-compliant" */
  summary: string;
  /** Items to display in the popover list */
  items: StatusListItem[];
  /** Hide the component entirely when there are no items */
  hideWhenEmpty?: boolean;
  /** Title shown at the top of the popover. Defaults to "Non-Compliant Items" */
  popoverTitle?: string;
  /**
   * When provided, overrides the default popover behaviour. Clicking the trigger
   * calls this instead of opening the internal popover. The parent is responsible
   * for showing whatever UI it wants at the click position.
   */
  onTriggerClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

const ExpandableStatusListImpl: React.FC<Props> = ({
  className,
  summary,
  items,
  hideWhenEmpty = false,
  popoverTitle = 'Non-Compliant Items',
  onTriggerClick,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (onTriggerClick) {
      onTriggerClick(event);
      return;
    }
    if (items.length > 0) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (hideWhenEmpty && items.length === 0) {
    return null;
  }

  // Interactive when an external override is provided OR when there are items to show
  const hasItems = Boolean(onTriggerClick) || items.length > 0;

  return (
    <span className={className}>
      <Box
        component="span"
        className="esl-trigger"
        onClick={handleOpen}
        role={hasItems ? 'button' : undefined}
        tabIndex={hasItems ? 0 : undefined}
        aria-haspopup={hasItems ? 'true' : undefined}
        aria-expanded={hasItems ? open : undefined}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (hasItems && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            // onTriggerClick requires a mouse event with position; skip for keyboard
            if (!onTriggerClick) {
              setAnchorEl(e.currentTarget as HTMLElement);
            }
          }
        }}
        sx={{
          cursor: hasItems ? 'pointer' : 'default',
        }}
      >
        <Text
          sz="body12"
          color="secondary"
          component="span"
          className="esl-trigger-text"
        >
          {summary}
        </Text>
        {hasItems && <InfoOutlinedIcon className="esl-trigger-icon" />}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              maxHeight: 360,
              borderRadius: '12px',
              boxShadow: theme.shadows[8],
              border: `1px solid ${theme.palette.divider}`,
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.03)'
                : 'rgba(0,0,0,0.02)',
          }}
        >
          <Text
            sz="body12"
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'text.secondary',
            }}
          >
            {popoverTitle}
          </Text>
          <Text
            sz="body12"
            sx={{ fontSize: '11px', color: 'text.disabled', mt: 0.25 }}
          >
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </Text>
        </Box>

        {/* Scrollable list */}
        <Box
          sx={{
            maxHeight: 280,
            overflowY: 'auto',
            py: 0.5,
          }}
        >
          {items.map((item, idx) => (
            <React.Fragment key={idx}>
              <Box
                onClick={
                  item.onClick
                    ? (e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleClose();
                        item.onClick!();
                      }
                    : undefined
                }
                role={item.onClick ? 'button' : undefined}
                tabIndex={item.onClick ? 0 : undefined}
                onKeyDown={
                  item.onClick
                    ? (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleClose();
                          item.onClick!();
                        }
                      }
                    : undefined
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 2.5,
                  py: 1,
                  minHeight: 40,
                  transition: 'background-color 0.15s ease',
                  ...(item.onClick && {
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.06)'
                          : 'rgba(0,0,0,0.04)',
                    },
                    '&:active': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.1)'
                          : 'rgba(0,0,0,0.06)',
                    },
                  }),
                }}
              >
                {/* Name with dot indicator */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: item.onClick
                        ? theme.palette.primary.main
                        : 'text.disabled',
                    }}
                  />
                  <Text
                    sz="body12"
                    sx={{
                      fontSize: '13px',
                      lineHeight: 1.4,
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: item.onClick
                        ? theme.palette.primary.main
                        : 'text.primary',
                      ...(item.onClick && {
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }),
                    }}
                  >
                    {item.name}
                  </Text>
                </Box>

                {/* Status chip */}
                <Chip
                  label={item.status}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    flexShrink: 0,
                    color: '#fff',
                    backgroundColor: item.color,
                    borderRadius: '6px',
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              </Box>
              {idx < items.length - 1 && (
                <Divider
                  sx={{
                    mx: 2.5,
                    opacity: 0.5,
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Popover>
    </span>
  );
};

const ExpandableStatusList = muiStyled(ExpandableStatusListImpl)(({
  theme,
}) => {
  return css`
    display: inline;

    .esl-trigger {
      display: inline-flex;
      align-items: center;
      gap: 4px;

      &:hover .esl-trigger-text {
        text-decoration: underline;
      }
    }

    .esl-trigger-text {
      font-size: 12px;
      line-height: 1.4;
    }

    .esl-trigger-icon {
      font-size: 14px;
      color: ${theme.palette.text.secondary};
      opacity: 0.7;
      transition: opacity 0.15s ease;

      .esl-trigger:hover & {
        opacity: 1;
      }
    }
  `;
});

export default ExpandableStatusList;
